import { useState, useRef, useEffect } from "react";
import { X, Send, User, Phone, LifeBuoy, Paperclip, Loader2 } from "lucide-react";
import api, { WS_BASE, fileUrl } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const FLORA_AVATAR = "/flora.png";
const SERVICE_PHONE = "04 42 69 02 13";

function getVid() {
  let v = localStorage.getItem("aj_vid");
  if (!v) { v = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem("aj_vid", v); }
  return v;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-1" data-testid="flora-typing">
      <span className="h-2 w-2 rounded-full bg-brand/70 animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="h-2 w-2 rounded-full bg-brand/70 animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="h-2 w-2 rounded-full bg-brand/70 animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

export default function ChatWidget() {
  const { customer } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const fileRef = useRef(null);

  const firstName = customer && customer.name ? customer.name.trim().split(" ")[0] : "";

  useEffect(() => {
    if (!open || sessionId) return;
    const body = customer
      ? { visitor_id: getVid(), customer: { id: customer.id, name: customer.name, email: customer.email, phone: customer.phone || "" } }
      : { visitor_id: getVid() };
    api.post("/chat/session", body).then((r) => {
      setSessionId(r.data.session_id);
      const hi = firstName
        ? `Bonjour ${firstName} 🌿 Je suis Flora, votre conseillère Acanthe Jardins. Ravie de vous retrouver ! Comment puis-je vous aider aujourd'hui ?`
        : "Bonjour 🌿 Je suis Flora, votre conseillère Acanthe Jardins. Dites-moi tout : un projet d'aménagement, une question sur nos végétaux, ou besoin d'un devis personnalisé gratuit ?";
      setMessages([{ role: "assistant", content: hi }]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const ws = new WebSocket(`${WS_BASE}/ws/chat/${sessionId}?role=visitor`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === "delta") {
        setThinking(false);
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant" && last.streaming) copy[copy.length - 1] = { ...last, content: last.content + data.content };
          else copy.push({ role: "assistant", content: data.content, streaming: true });
          return copy;
        });
      } else if (data.type === "done") {
        setStreaming(false);
        setThinking(false);
        setMessages((m) => m.map((x) => (x.streaming ? { ...x, streaming: false } : x)));
      } else if (data.type === "message" && data.role === "agent") {
        setStreaming(false);
        setThinking(false);
        setMessages((m) => [...m, { role: "agent", content: data.content, image: data.image }]);
      } else if (data.type === "status") {
        setHandoff(data.agent_online);
        setThinking(false);
        setMessages((m) => [...m, {
          role: "assistant",
          content: data.agent_online
            ? "Je vous mets en relation avec un conseiller, un instant 🌿"
            : `Aucun conseiller n'est disponible tout de suite, mais votre demande est bien enregistrée. Nous vous recontactons vite ! Vous pouvez aussi nous appeler au ${SERVICE_PHONE}.`,
        }]);
      }
    };
    return () => { ws.close(); wsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming, thinking]);

  const send = () => {
    const text = input.trim();
    if (!text || streaming || wsRef.current?.readyState !== WebSocket.OPEN) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming(true);
    setThinking(true);
    wsRef.current.send(JSON.stringify({ type: "message", content: text }));
  };

  const uploadPhoto = async (file) => {
    if (!file || !sessionId || wsRef.current?.readyState !== WebSocket.OPEN) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("session_id", sessionId);
      const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, { role: "user", content: "", image: data.path }]);
      setStreaming(true);
      setThinking(true);
      wsRef.current.send(JSON.stringify({ type: "message", content: "", image: data.path }));
    } catch { /* ignore */ } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const startSav = () => {    if (streaming || wsRef.current?.readyState !== WebSocket.OPEN) return;
    const text = "Je souhaite créer un ticket SAV (service après-vente) pour un problème.";
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming(true);
    setThinking(true);
    wsRef.current.send(JSON.stringify({ type: "message", content: text }));
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-toggle" aria-label="Discuter avec Flora"
        className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full bg-forest text-cream shadow-2xl shadow-forest/40 flex items-center justify-center hover:scale-105 transition-transform overflow-hidden ring-2 ring-brand/60">
        {open ? <X size={26} /> : <img src={FLORA_AVATAR} alt="Flora" className="h-full w-full object-cover" />}
      </button>

      {!open && (
        <div className="fixed bottom-[5.6rem] right-6 z-40 hidden sm:flex items-center gap-1.5 bg-white rounded-full pl-1 pr-3 py-1 shadow-lg border border-border animate-pulse" data-testid="flora-bubble">
          <img src={FLORA_AVATAR} alt="" className="h-6 w-6 rounded-full object-cover" />
          <span className="text-xs font-medium text-forest">Une question ? Je suis Flora 🌿</span>
        </div>
      )}

      {open && (
        <div data-testid="chat-window"
          className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[540px] max-h-[75vh] rounded-2xl overflow-hidden bg-cream/95 backdrop-blur-xl border border-border shadow-2xl flex flex-col">
          <div className="bg-forest text-cream px-5 py-4 flex items-center gap-3">
            <img src={FLORA_AVATAR} alt="Flora" onClick={() => setShowProfile(true)} data-testid="flora-header-avatar" className="h-11 w-11 rounded-full object-cover ring-2 ring-brand/70 cursor-pointer hover:ring-brand transition" title="Voir Flora" />
            <div className="flex-1">
              <p className="font-semibold text-sm flex items-center gap-1.5">Flora <span className="h-2 w-2 rounded-full bg-green-400 inline-block" /></p>
              <p className="text-[11px] text-cream/70">Votre conseillère Acanthe Jardins</p>
            </div>
          </div>

          {showProfile && (
            <div className="absolute inset-0 z-10 bg-forest/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-cream" data-testid="flora-profile" onClick={() => setShowProfile(false)}>
              <button onClick={() => setShowProfile(false)} data-testid="flora-profile-close" className="absolute top-4 right-4 text-cream/80 hover:text-cream"><X size={22} /></button>
              <img src={FLORA_AVATAR} alt="Flora" className="h-40 w-40 rounded-full object-cover ring-4 ring-brand/70 shadow-2xl" onClick={(e) => e.stopPropagation()} />
              <p className="mt-4 font-display text-2xl">Flora</p>
              <p className="text-sm text-cream/70">Votre conseillère Acanthe Jardins</p>
              <a href={`tel:${SERVICE_PHONE.replace(/\s/g, "")}`} data-testid="flora-profile-phone" onClick={(e) => e.stopPropagation()}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand text-cream px-6 py-3 font-semibold hover:bg-brand/90 transition shadow-lg">
                <Phone size={18} /> {SERVICE_PHONE}
              </a>
              <p className="mt-3 text-xs text-cream/60">Service client — du lundi au vendredi</p>
            </div>
          )}

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && <img src={FLORA_AVATAR} alt="" className="h-7 w-7 rounded-full object-cover shrink-0 mb-0.5" />}
                <div data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-brand text-cream rounded-br-sm"
                    : m.role === "agent" ? "bg-terracotta/20 text-forest rounded-bl-sm border border-terracotta/40"
                    : "bg-white text-forest rounded-bl-sm border border-border"}`}>
                  {m.role === "agent" && <span className="text-[10px] font-bold text-terracotta mb-0.5 flex items-center gap-1"><User size={10}/>Conseiller</span>}
                  {m.image && <img src={fileUrl(m.image)} alt="photo" className="rounded-lg mb-1 max-h-40 object-cover" data-testid="chat-img" />}
                  {m.content ? m.content : (!m.image && <span className="opacity-40">…</span>)}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex items-end gap-2 justify-start">
                <img src={FLORA_AVATAR} alt="" className="h-7 w-7 rounded-full object-cover shrink-0 mb-0.5" />
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4"><TypingDots /></div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3 bg-white">
            {handoff === null && (
              <div className="flex items-center justify-between mb-2 px-1">
                <button onClick={startSav} data-testid="chat-create-sav"
                  className="text-xs font-semibold text-brand hover:text-forest flex items-center gap-1">
                  <LifeBuoy size={13} /> Créer un ticket SAV
                </button>
                <a href={`tel:${SERVICE_PHONE.replace(/\s/g, "")}`} data-testid="chat-call-service"
                  className="text-xs font-semibold text-stone hover:text-forest flex items-center gap-1">
                  <Phone size={13} /> {SERVICE_PHONE}
                </a>
              </div>
            )}
            <div className="flex gap-2 items-center">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} data-testid="chat-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading || !sessionId} data-testid="chat-attach" title="Joindre une photo"
                className="h-10 w-10 shrink-0 rounded-full border border-border text-stone flex items-center justify-center hover:text-brand hover:border-brand transition-colors disabled:opacity-50">
                {uploading ? <Loader2 className="animate-spin" size={17} /> : <Paperclip size={17} />}
              </button>
              <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Écrivez à Flora…" data-testid="chat-input"
                className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
              <button onClick={send} disabled={streaming} data-testid="chat-send"
                className="h-10 w-10 shrink-0 rounded-full bg-brand text-cream flex items-center justify-center hover:bg-forest transition-colors disabled:opacity-50">
                <Send size={17} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
